import type React from "react";
import { createContext } from "react";
import { TimeSlotSelectFilterChoiceEnum } from "src/components-ds/filters";
import type { Element as SelectElement } from "src/components/generics/nestedSelect/NestedSelect";
import {
    type LocalTableDisplayConfiguration,
    TableConfigurationDisplayEnum,
} from "src/components/Table2";
import { CountryEnum } from "src/types/country";
import { CompareMethodKeyEnum } from "src/types/enum/CompareMethodKey";
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import { RangeType } from "src/types/enum/RangeType";
import type { Id } from "src/types/id";
import type { OfferSavedHeaderFilters } from "src/types/offer/HeaderParameter";
import { PerformanceCompareConfigKey } from "src/types/performance/PerformanceCompareConfigKey";
import type { SponsoredSavedAdHeaderFilters } from "src/types/sponsored-ads/SponsoredSavedAdHeaderFilters";
import type { SuperpowerSavedHeaderFilters } from "src/types/superPowerHeaderParameter";
import { FilterTypeEnum } from "src/utils/tracking/types";
import {
    ComparisonMode,
    ComparisonType,
    RevenueBreakdown,
    TrendDuration,
    TrendScale,
} from "src/views-ds/dashboard/types";
import { DisplayModeEnum, KPIBannerKeyEnum } from "src/views-ds/welcome/welcome-view/types/enums";
import type { AnalyzeSavedHeaderFilters } from "src/views/admin/AdminView/filters/types";
import { type SettingsContextValue, SettingsProvider } from "./SettingsContext";

export interface DashboardPreferenceLocalSettings {
    initialPeriod: RangeType;
    comparisonType: ComparisonType;
    comparisonMode: ComparisonMode;
    trendDuration: TrendDuration;
    trendScale: TrendScale;
    revenueBreakdown: RevenueBreakdown;
}
export interface WelcomePagePreferenceLocalSettings {
    initialPeriod: RangeType;
    displayMode: DisplayModeEnum;
    visibleKpis?: KPIBannerKeyEnum[];
}

export interface MarketingActionDashboardPreferenceLocalSettings {
    initialPeriod: RangeType;
    comparisonType: ComparisonType;
    marketingActionComparisonType: PerformanceCompareConfigKey;
}
export interface LocalSettings {
    direction?: "ltr" | "rtl";
    responsiveFontSizes?: boolean;
    theme?: string;
    offerDetailTableConf?: LocalTableDisplayConfiguration;
    offerPerformanceTableConf?: LocalTableDisplayConfiguration;
    sponsoredAdDetailTableConf?: LocalTableDisplayConfiguration;
    sponsoredAdPerformanceTableConf?: LocalTableDisplayConfiguration;
    offerHeaderFilters?: OfferSavedHeaderFilters;
    sponsoredAdHeaderFilters?: SponsoredSavedAdHeaderFilters;
    selectedElement?: SelectElement;
    possIds?: Id[];
    offerTotalSwitch?: boolean;
    sponsoredAdTotalSwitch?: boolean;
    compareKey?: PerformanceCompareConfigKey;
    version?: string;
    welcomeCompareMethodKey?: CompareMethodKeyEnum;
    disabledWelcomeCompareExplanationDialogDisplay?: boolean;
    disabledNewOverridesExplanationDialogDisplay?: boolean;
    superPowerHeaderFilters?: SuperpowerSavedHeaderFilters;
    posGroupIdList?: Id[];
    analyzeHeaderFilters?: AnalyzeSavedHeaderFilters;
    dashboardPreferences?: DashboardPreferenceLocalSettings;
    marketingActionDashboardPreferences?: {
        [MarketingActionType.Offer]: MarketingActionDashboardPreferenceLocalSettings;
        [MarketingActionType.SponsoredAd]: MarketingActionDashboardPreferenceLocalSettings;
    };
    welcomePagePreferences?: WelcomePagePreferenceLocalSettings;
}

export type LocalSettingsKeys = keyof LocalSettings;

const defaultVisibleKpis: KPIBannerKeyEnum[] = [
    KPIBannerKeyEnum.revenue,
    KPIBannerKeyEnum.profit,
    KPIBannerKeyEnum.efficiencyScore,
    KPIBannerKeyEnum.roas,
    KPIBannerKeyEnum.openRate,
];

export const DEFAULT_SETTINGS: LocalSettings = {
    direction: "ltr",
    responsiveFontSizes: true,
    theme: "LIGHT",
    offerTotalSwitch: false,
    sponsoredAdTotalSwitch: false,
    compareKey: PerformanceCompareConfigKey.OneMonth,
    version: import.meta.env.VITE_SETTINGS_VERSION,
    welcomeCompareMethodKey: CompareMethodKeyEnum.Cumulative,
    offerDetailTableConf: { activeDisplay: TableConfigurationDisplayEnum.Basic, customDisplay: {} },
    offerPerformanceTableConf: {
        activeDisplay: TableConfigurationDisplayEnum.Basic,
        customDisplay: {},
    },
    sponsoredAdDetailTableConf: {
        activeDisplay: TableConfigurationDisplayEnum.Basic,
        customDisplay: {},
    },
    sponsoredAdPerformanceTableConf: {
        activeDisplay: TableConfigurationDisplayEnum.Basic,
        customDisplay: {},
    },
    disabledWelcomeCompareExplanationDialogDisplay: false,
    analyzeHeaderFilters: { organizationIdList: [], userIdList: [], country: CountryEnum.FR },
    dashboardPreferences: {
        initialPeriod: RangeType.onGoingWeek,
        comparisonType: ComparisonType.previousPeriod,
        comparisonMode: ComparisonMode.auto,
        trendDuration: TrendDuration.completePeriod,
        trendScale: TrendScale.global,
        revenueBreakdown: RevenueBreakdown.global,
    },
    welcomePagePreferences: {
        initialPeriod: RangeType.lastWeek,
        displayMode: DisplayModeEnum.Simple,
        visibleKpis: defaultVisibleKpis,
    },
    marketingActionDashboardPreferences: {
        [MarketingActionType.Offer]: {
            initialPeriod: RangeType.last30DaysAndToday,
            comparisonType: ComparisonType.previousPeriod,
            marketingActionComparisonType: PerformanceCompareConfigKey.OneMonth,
        },
        [MarketingActionType.SponsoredAd]: {
            initialPeriod: RangeType.last30DaysAndToday,
            comparisonType: ComparisonType.previousPeriod,
            marketingActionComparisonType: PerformanceCompareConfigKey.OneMonth,
        },
    },
    offerHeaderFilters: {
        serviceTime: TimeSlotSelectFilterChoiceEnum.AllDay,
        audiences: [],
        channels: [],
        days: [],
        poss: [],
        status: [],
        types: [],
        minBaskets: [],
        flatDiscounts: [],
        percentDiscounts: [],
        actionTypes: [],
        checkBoxInnerLabelSelectionList: [FilterTypeEnum.Pos, FilterTypeEnum.Channel],
        timeSpendList: [],
    },
};

export const LocalSettingsContext = createContext<SettingsContextValue<LocalSettings>>({
    settings: DEFAULT_SETTINGS,
    clearSettings: () => {
        // RAF
    },
    saveSettings: () => {
        // RAF;
    },
    setUserInfos: () => {
        // RAF;
    },
});

interface LocalSettingsProviderProps {
    children: React.ReactNode;
}

export function LocalSettingsProvider({ children }: LocalSettingsProviderProps): JSX.Element {
    return (
        <SettingsProvider<LocalSettings>
            Context={LocalSettingsContext}
            storageManager={window.localStorage}
            defaultSettings={DEFAULT_SETTINGS}
        >
            {children}
        </SettingsProvider>
    );
}
